import { useEffect, useMemo } from "react"
import useCourses from "../../hooks/use-courses"
import { parseAttributes } from "../../lib/utils"
import {
	SpecializationFieldConfig,
	type Attribute,
	type FieldProps,
} from "../../types/form-types"
import { SelectField } from "./select"
import { useFormStore } from "@/stores/use-form-store"
import { logger } from "@/utils/logger"

export const SpecializationField = ({
	form,
	field,
	commonAttributes,
}: FieldProps) => {
	const { programTypeFieldId, programFieldId } =
		getSpecializationFieldAttributes(field.attributes)

	const {
		config,
		submissionValues,
		applicationId,
		applicationSubmission,
		verifiedFields,
	} = useFormStore()

	const selectedProgramType = form.watch(programTypeFieldId!)
	const selectedProgram =
		form.watch(programFieldId!) || applicationSubmission[programFieldId!]

	logger.info("selected program: ", selectedProgram)

	const { useGetSpecializations } = useCourses()
	const { data: specializations } = useGetSpecializations({
		universityId: config?.university_id ?? "",
		batchId: config?.batch_id ?? "",
		applicationId: applicationId ?? "",
		programId: selectedProgram,
		type: config?.type ?? "FORM",
		widgetId: config?.widget_id ?? "",
	})

	const options = useMemo(() => {
		if (!selectedProgram || !specializations) return []

		return specializations.map(specialization => ({
			label: specialization.label,
			value: specialization.value,
		}))
	}, [specializations, selectedProgram])

	useEffect(() => {
		if (submissionValues && submissionValues[field.field_id]) {
			const value = submissionValues[field.field_id]
			if (value != "") form.setValue(field.field_id, value)
		}
	}, [options, submissionValues])

	return (
		<SelectField
			form={form}
			field={field}
			commonAttributes={commonAttributes}
			optionsPreSet={options}
			isDisabled={
				!selectedProgramType ||
				!selectedProgram ||
				!specializations ||
				verifiedFields.has(field.field_id)
			}
		/>
	)
}

function getSpecializationFieldAttributes(attributes: Attribute[]) {
	const props: {
		programTypeFieldId?: string
		programFieldId?: string
	} = {}

	const parsedAttributes = parseAttributes(attributes)

	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case SpecializationFieldConfig.ProgramTypeFieldId:
				props.programTypeFieldId = value as string
				break
			case SpecializationFieldConfig.ProgramFieldId:
				props.programFieldId = value as string
				break
		}
	})

	return props
}
